import { format } from "date-fns";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useQueryString } from "../../hooks/use_query_string";
import { localesByLanguage } from "../../offering/locale_data";
import { formatCurrency as libFormatCurrency } from "../currency/currency";
import { he, enUS, pl } from "date-fns/locale";
import {
  getLanguageComponent,
  getSystemLocale,
  normalizeLanguage,
} from "../locale/locale";
import { isEmpty } from "../js_utils/lang_utils";
import { merge } from "lodash";
import { enLocale } from "@/offering/locale_data";
import {
  classifyResolution,
  installI18nInspector,
  markPartnerKeys,
  recordResolution,
} from "./i18n_inspector";
import type { ResolutionTrace } from "./i18n_inspector";

export interface Locale {
  languageTag: string;
  baseLanguageTag: string;
  direction: "ltr" | "rtl";
}

type FBTTranslateFunc = (
  text: string,
  options?: { description?: string }
) => string;
type FBTPluralFunc = (
  singular: string,
  count: number,
  options: { many: string; name: string; showCount?: "yes" | "no" | "ifMany" }
) => string;
type FBTParamFunc = (name: string, value: string | number) => string;
type FBTFormatDateByPatternFunc = (date: Date, pattern: string) => string;
type FBTFormatDateFunc = (
  date: Date,
  options?: Intl.DateTimeFormatOptions
) => string;
type FBTFormatTimeFunc = (date: Date) => string;
type FBTFormatCurrencyFunc = (
  amount: number,
  currency: string | null
) => string;
type FBTTranslateOptions = { description?: string };

export interface FBT {
  (text: string, options?: FBTTranslateOptions): string;
  plural: FBTPluralFunc;
  param: FBTParamFunc;
  formatDateByPattern: FBTFormatDateByPatternFunc;
  formatDate: FBTFormatDateFunc;
  formatTime: FBTFormatTimeFunc;
  formatCurrency: FBTFormatCurrencyFunc;
}

interface I18nContext {
  locale: Locale;
  onChangeLocale: (locale: Locale) => void;
  onAddToDictionary: (dictionary: Dictionary) => void;
  fbt: FBT;
}

const initialFBT: FBT = Object.assign(() => {
  return "";
});

const I18nContext = createContext<I18nContext>({
  locale: {
    languageTag: getSystemLocale(),
    baseLanguageTag: getLanguageComponent(getSystemLocale()),
    direction: "ltr",
  },
  onChangeLocale: () => {},
  onAddToDictionary: () => {},
  fbt: initialFBT,
});

interface I18nProviderProps {
  fallbackLocale: Locale;
  initialDictionary?: Dictionary;
  children?: React.ReactNode;
}

export interface Dictionary {
  [languageTag: string]: {
    [text: string]: string;
  };
}

const LOCALE_KEY = "flexspace:locale";

function isLanguageWhitelisted(locale: string) {
  const whitelist = Object.keys(localesByLanguage);

  return whitelist.some((l) => locale === l);
}

function getDefaultLocale(
  qs: { [queryParams: string]: string | undefined },
  fallbackLocale: Locale
) {
  let qsLang = qs.lang;

  if (qsLang) {
    qsLang = normalizeLanguage(qsLang);

    if (!isLanguageWhitelisted(qsLang)) {
      localStorage.setItem(LOCALE_KEY, "en");
      return fallbackLocale;
    }

    localStorage.setItem(LOCALE_KEY, qsLang);
    return localesByLanguage[qsLang];
  }

  let storageLang = localStorage.getItem(LOCALE_KEY);

  if (storageLang) {
    storageLang = normalizeLanguage(storageLang);
    if (!isLanguageWhitelisted(storageLang)) {
      localStorage.setItem(LOCALE_KEY, "en");
      return fallbackLocale;
    }

    return localesByLanguage[storageLang];
  }

  let systemLang = getSystemLocale();
  systemLang = normalizeLanguage(systemLang);

  if (!isLanguageWhitelisted(systemLang)) {
    localStorage.setItem(LOCALE_KEY, "en");
    return fallbackLocale;
  }

  const locale = localesByLanguage[systemLang];

  if (!locale) {
    localStorage.setItem(LOCALE_KEY, "en");
    return fallbackLocale;
  }

  localStorage.setItem(LOCALE_KEY, locale.languageTag);
  return locale;
}

const tokenRegex = RegExp(/{(.+?)}/g);

const TRANSLATION_WHITELIST = [
  "wework",
  "mdot",
  "rooms",
  "brain-embassy",
  "coventus",
  "urbanplace",
];

// Translation is a white-label offering feature. The member self-serve portal
// shares the partner slug that the whitelist matches on (e.g. /portal/wework),
// so without this exclusion it would inherit the visitor's saved offering
// locale — flipping the untranslated portal to RTL and picking up stray
// Hebrew/Polish strings.
const MEMBER_PORTAL_PATH_PREFIX = "/portal/";

export function isTranslatableSurface(pathname: string) {
  if (pathname.startsWith(MEMBER_PORTAL_PATH_PREFIX)) return false;

  return TRANSLATION_WHITELIST.some((pn) => pathname.includes(pn));
}

export function I18nProvider(props: I18nProviderProps) {
  const { children, fallbackLocale, initialDictionary = {} } = props;
  const qs = useQueryString();
  const [locale, setLocale] = useState<Locale>(
    isTranslatableSurface(window.location.pathname)
      ? getDefaultLocale(qs, fallbackLocale)
      : fallbackLocale
  );
  const [dictionary, setDictionary] = useState<Dictionary>(initialDictionary);

  const translate: FBTTranslateFunc = useCallback(
    (text, options) => {
      return fbtTranslate(dictionary, locale, fallbackLocale)(text, options);
    },
    [dictionary, locale, fallbackLocale]
  );

  const param: FBTParamFunc = useCallback((name, value) => {
    return fbtParam(name, value);
  }, []);

  const plural: FBTPluralFunc = useCallback(
    (singular, count, options) => {
      return fbtPlural(param)(singular, count, options);
    },
    [param]
  );

  const formatDateByPattern: FBTFormatDateByPatternFunc = useCallback(
    (date, formatPattern) => {
      const lang = getLanguageComponent(locale.languageTag);
      if (lang === "he") {
        return format(date, formatPattern, { locale: he });
      } else if (lang === "pl") {
        return format(date, formatPattern, { locale: pl });
      }

      return format(date, formatPattern, { locale: enUS });
    },
    [locale.languageTag]
  );

  const formatTime: FBTFormatTimeFunc = useCallback(
    (date) => {
      return date.toLocaleTimeString(locale.languageTag, {
        hour: "numeric",
        minute: "numeric",
      });
    },
    [locale.languageTag]
  );

  const formatDate: FBTFormatDateFunc = useCallback(
    (date, options) => {
      return date.toLocaleDateString(
        locale.languageTag,
        options || {
          month: "short",
          day: "numeric",
        }
      );
    },
    [locale.languageTag]
  );

  const formatCurrency: FBTFormatCurrencyFunc = useCallback(
    (amount, currency) => {
      return libFormatCurrency(amount, currency, locale.languageTag);
    },
    [locale.languageTag]
  );

  const handleAddToDictionary = useCallback((nextDictionary) => {
    // Keys only ever arrive here from per-partner override files, so the dev
    // inspector tags them as partner overrides (base copy is seeded separately).
    if (import.meta.env.DEV) {
      markPartnerKeys(nextDictionary);
    }
    // merge({}, …) returns a fresh ref: lodash merge mutates its first arg in
    // place, which would keep the same ref and let React skip the re-render.
    setDictionary((dictionary) => merge({}, dictionary, nextDictionary));
  }, []);

  const handleChangeLocale = useCallback((locale: Locale) => {
    setLocale(locale);
    localStorage.setItem(LOCALE_KEY, locale.languageTag);
  }, []);

  useEffect(() => {
    document.documentElement.dir = locale.direction;
    document.documentElement.lang = locale.baseLanguageTag;
  }, [locale]);

  useEffect(() => {
    if (import.meta.env.DEV) {
      installI18nInspector();
    }
  }, []);

  const fbt: FBT = useMemo(
    () =>
      Object.assign(
        translate,
        { param },
        { plural },
        { formatDateByPattern },
        { formatDate },
        { formatCurrency },
        { formatTime }
      ),
    [
      translate,
      param,
      plural,
      formatDateByPattern,
      formatDate,
      formatCurrency,
      formatTime,
    ]
  );

  return (
    <I18nContext.Provider
      value={{
        locale,
        onChangeLocale: handleChangeLocale,
        onAddToDictionary: handleAddToDictionary,
        fbt,
      }}
    >
      {children}
    </I18nContext.Provider>
  );
}

/**
 * This function translates strings.
 *
 * In case of interpolation, e.g. fbt('Hello, ' + fbt.param('name', value))
 * The resulting string is "Hello, {name:value}"
 *
 * Translation tries to replace the token `name` with `value` such that
 * given `value=Peter` it becomes "Hello, Peter"
 */
export function fbtTranslate(
  dictionary: Dictionary,
  locale: Locale,
  fallbackLocale?: Locale
): FBTTranslateFunc {
  return function translate(text, options) {
    const context = options?.description;
    const [marshalledText, tokens] = getTokenizedTextAndTokens(text);
    // Only allocated for dev-inspector builds; undefined skips all tracing.
    const trace: ResolutionTrace | undefined = import.meta.env.DEV
      ? { key: "", tag: "" }
      : undefined;
    const record = (source: string, value: string) => {
      if (!import.meta.env.DEV) return;
      recordResolution(
        classifyResolution({
          source,
          context,
          value,
          locale,
          fallbackLocale,
          trace,
        })
      );
    };

    // Check if there is a translation for tokenized strings with specific values
    // e.g. tokenized string "{number of likes} likes"
    // user translated "2 likes" to "II likes" instead of leaving default
    if (!isEmpty(tokens)) {
      let str = marshalledText;

      Object.keys(tokens).forEach((token) => {
        const value = tokens[token];

        str = str.replace(`{${token}}`, value);
      });

      let trs = getContextualTranslation(
        str,
        context,
        dictionary,
        locale,
        fallbackLocale,
        false,
        trace
      );

      if (trs) {
        record(str, trs);
        return trs;
      }
    }

    let translation = getContextualTranslation(
      marshalledText,
      context,
      dictionary,
      locale,
      fallbackLocale,
      true,
      trace
    );

    if (translation === false) {
      if (!isEmpty(tokens)) {
        let result = marshalledText;
        Object.keys(tokens).forEach((token) => {
          result = result.replace(`{${token}}`, String(tokens[token]));
        });
        record(marshalledText, result);
        return result;
      }
      record(marshalledText, marshalledText);
      return marshalledText;
    }

    if (!isEmpty(tokens)) {
      Object.keys(tokens).forEach((token) => {
        const value = tokens[token];

        if (typeof translation === "string") {
          translation = translation.replace(`{${token}}`, value);
        }
      });
    }

    record(marshalledText, translation);
    return translation;
  };
}

function getTokenizedTextAndTokens(
  text: string
): [string, { [key: string]: string }] {
  let marshalledText = text;

  const tokens: { [key: string]: string } = {};

  let match;
  while ((match = tokenRegex.exec(text)) !== null) {
    const separatorIndex = match[1].indexOf(":");
    const token = match[1].substr(0, separatorIndex);
    const value = match[1].substr(separatorIndex + 1);

    marshalledText = marshalledText.replace(match[0], `{${token}}`);
    tokens[token] = value;
  }

  return [marshalledText, tokens];
}

// Prefers a "<context>::<source>" key over the plain source, so one surface can
// reword a shared string without changing it everywhere; falls back otherwise.
// The scoped lookup omits the not-found warning — a scoped miss is the common
// case (most strings have no override) and would otherwise spam the console.
function getContextualTranslation(
  text: string,
  context: string | undefined,
  dictionary: Dictionary,
  locale: Locale,
  fallbackLocale?: Locale,
  checkIfNotFound?: boolean,
  trace?: ResolutionTrace
): string | false {
  if (context) {
    const scoped = getTranslation(
      `${context}::${text}`,
      dictionary,
      locale,
      fallbackLocale,
      false,
      trace
    );

    if (scoped) {
      return scoped;
    }
  }

  return getTranslation(
    text,
    dictionary,
    locale,
    fallbackLocale,
    checkIfNotFound,
    trace
  );
}

// `trace`, when supplied (dev inspector only), captures which dictionary entry a
// lookup landed on so the resolution can be classified base/partner/fallback.
function getTranslation(
  text: string,
  dictionary: Dictionary,
  locale: Locale,
  fallbackLocale?: Locale,
  checkIfNotFound?: boolean,
  trace?: ResolutionTrace
): string | false {
  if (text === "") {
    // no translation for empty string
    return false;
  }

  const localeBaseTag = getLanguageComponent(locale.languageTag);

  if (dictionary[locale.languageTag] && dictionary[locale.languageTag][text]) {
    return found(
      text,
      locale.languageTag,
      dictionary[locale.languageTag][text],
      trace
    );
  } else if (dictionary[localeBaseTag] && dictionary[localeBaseTag][text]) {
    return found(text, localeBaseTag, dictionary[localeBaseTag][text], trace);
  } else if (
    fallbackLocale &&
    dictionary[fallbackLocale.languageTag] &&
    dictionary[fallbackLocale.languageTag][text]
  ) {
    return found(
      text,
      fallbackLocale.languageTag,
      dictionary[fallbackLocale.languageTag][text],
      trace
    );
  } else if (
    fallbackLocale &&
    dictionary[getLanguageComponent(fallbackLocale.languageTag)] &&
    dictionary[getLanguageComponent(fallbackLocale.languageTag)][text]
  ) {
    const fallbackBaseTag = getLanguageComponent(fallbackLocale.languageTag);
    return found(
      text,
      fallbackBaseTag,
      dictionary[fallbackBaseTag][text],
      trace
    );
  }

  if (checkIfNotFound && locale.languageTag !== enLocale.languageTag) {
    console.log("Translation not found for", text);
  }
  return false;
}

function found(
  key: string,
  tag: string,
  value: string,
  trace?: ResolutionTrace
): string {
  if (trace) {
    trace.key = key;
    trace.tag = tag;
  }
  return value;
}

export const fbtParam: FBTParamFunc = function fbtParam(
  name: string,
  value: string | number
) {
  return `{${name}:${value}}`;
};

export function fbtPlural(param: FBTParamFunc): FBTPluralFunc {
  return function plural(singular, count, options) {
    if (count > 1) {
      if (options.showCount === "ifMany" || options.showCount === "yes") {
        return `${param(options.name, count)} ${options.many}`;
      }

      return options.many;
    }

    if (count === 0) {
      return `${param(options.name, count)} ${options.many}`;
    }

    if (options.showCount === "yes") {
      return `1 ${singular}`;
    }

    return singular;
  };
}

export function useI18n() {
  return useContext(I18nContext);
}
